import React from 'react'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/styles'
import { useTheme } from '@material-ui/styles'
import { imageUrlRoot, imageUrls } from 'app/constants'
import { pathJoin } from 'utils/misc'
import Theme from 'core/themes/model'
import { prop } from 'ramda'
import { PreferencesState, preferencesStoreKey } from 'core/session/preferencesReducers'
import { useSelector } from 'react-redux'
import ImageWithFallback from 'core/components/image-fallback'

const useStyles = makeStyles<Theme>((theme) => ({
  page: {
    minWidth: '100vw',
    minHeight: '100vh',
    boxSizing: 'border-box',
    backgroundColor: theme?.components?.frame?.background,
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',

    '@media (max-width:992px)': {
      '& .form-page-footer': {
        maxWidth: 250,
      },
    },
  },
  container: {
    width: 1120,
    minHeight: 600,
    borderRadius: 16,
    border: `solid 1px ${theme?.components?.card?.border}`,
    display: 'grid',
    gridTemplateColumns: '50% 50%',
    overflow: 'hidden',

    '@media (max-width:1200px)': {
      width: 912,
      '& .left-pane': {
        padding: '0 58px',
      },
      '& .right-pane': {
        paddingLeft: 48,
        paddingRight: 48,
      },
    },
    '@media (max-width:992px)': {
      gridTemplateColumns: 'unset',
      maxWidth: 400,
      minHeight: 475,
      width: '100%',
      '& .left-pane': {
        display: 'none',
      },
    },
    '@media (max-width:480px)': {
      maxWidth: 360,
      '& .right-pane': {
        paddingLeft: 24,
        paddingRight: 24,
      },
    },
  },
  managementPlane: {
    backgroundColor: theme?.components?.frame?.accentBackground,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 80px',
    borderRight: `solid 1px ${theme?.components?.card?.border}`,
  },
  formPane: {
    position: 'relative',
    padding: '48px 24px',
    backgroundColor: theme?.components?.card?.background,
    display: 'grid',
    alignItems: 'center',
    justifyItems: 'center',
    gridGap: theme.spacing(2),
  },
  img: {
    maxWidth: '100%',
  },
  logo: {
    width: 240,
    marginBottom: theme.spacing(6),
  },
  footer: {
    marginTop: 16,
  },
}))

const images = {
  light: {
    logoUrl: imageUrls.lightLogoPrimary,
    planeUrl: imageUrls.lightManagementPane,
  },
  dark: {
    logoUrl: imageUrls.darkLogoPrimary,
    planeUrl: imageUrls.darkManagementPane,
  },
}

const FormPageContainer = ({ children, className = undefined, footer = undefined }) => {
  const classes = useStyles()
  const theme: Theme = useTheme()
  const { logoUrl } = useSelector(prop<string, PreferencesState>(preferencesStoreKey))

  const { logoUrl: defaultLogoUrl, planeUrl } = images[theme.palette.type]
  return (
    <section
      id={clsx('form-page-container', className)}
      className={clsx('form-page-container', classes.page)}
    >
      <ImageWithFallback
        src={logoUrl}
        fallbackSrc={defaultLogoUrl}
        alt="Platform9 Logo"
        className={classes.logo}
      />
      <article className={classes.container}>
        <div className={clsx('left-pane', classes.managementPlane)}>
          <img alt="Platform9 Management Plane" src={planeUrl} className={classes.img} />
        </div>
        <div className={clsx('right-pane', classes.formPane)}>{children}</div>
      </article>
      {footer && <footer className={clsx('form-page-footer', classes.footer)}>{footer}</footer>}
    </section>
  )
}

export default FormPageContainer
